import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getProperties, getDashboardStats } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { DollarSign, Home, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function OwnerPortal() {
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [propertiesData, statsData] = await Promise.all([
        getProperties(),
        getDashboardStats()
      ]);
      setProperties(propertiesData);
      setStats(statsData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div data-testid="owner-portal" className="max-w-7xl mx-auto animate-fade-in">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Welcome, Property Owner</h1>
          <p className="text-gray-600">Manage your property portfolio and view financial reports</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <Card data-testid="my-properties-stat">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">My Properties</CardTitle>
                  <Home className="w-5 h-5 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-gray-900">{stats.my_properties || 0}</div>
                </CardContent>
              </Card>
              
              <Card data-testid="total-rent-stat">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Rent</CardTitle>
                  <DollarSign className="w-5 h-5 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-gray-900">${stats.total_rent || 0}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Active Leases</CardTitle>
                  <FileText className="w-5 h-5 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-gray-900">{properties.filter(p => p.status === 'leased').length}</div>
                </CardContent>
              </Card>
            </div>

            <Card data-testid="properties-list">
              <CardHeader>
                <CardTitle>My Properties</CardTitle>
              </CardHeader>
              <CardContent>
                {properties.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No properties found</p>
                ) : (
                  <div className="space-y-4">
                    {properties.map((property) => (
                      <div key={property.id} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-gray-900">{property.address}</h3>
                            <p className="text-sm text-gray-600">{property.suburb}, {property.state}</p>
                          </div>
                          <span className={`badge ${property.status === 'leased' ? 'badge-success' : 'badge-info'}`}>
                            {property.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}